import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { UserContext } from '../UserContext'; // ✅ Import UserContext
import './BlogDetail.css';

const BlogDetail = () => {
    const { blogId } = useParams();
    const { user, isAuthenticated } = useContext(UserContext); // ✅ Get logged-in user

    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [commentContent, setCommentContent] = useState('');
    const [replyContent, setReplyContent] = useState({});
    const [expandedReplies, setExpandedReplies] = useState({});
    const [showReplyInput, setShowReplyInput] = useState({});

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                console.log('[FETCH] Getting blog details...');
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/blog/${blogId}`);
                setBlog(response.data);
            } catch (err) {
                console.error('[ERROR] Fetching blog:', err);
                setError(err.response?.status === 404 ? 'Blog not found' : 'Error fetching blog details');
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [blogId]);

    // ✅ Add Comment (Using logged-in user)
    const addComment = async () => {
        if (!commentContent.trim()) return;
        if (!isAuthenticated || !user?.id) {
            setError('You must be logged in to comment.');
            return;
        }

        try {
            console.log('[POST] Adding comment...');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/blog/${blogId}/comment`,
                { content: commentContent, author: user.username }, // ✅ Send username
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            console.log('[SUCCESS] Comment added:', response.data);
            setBlog((prevBlog) => ({
                ...prevBlog,
                comments: [...prevBlog.comments, response.data],
            }));

            setCommentContent('');
        } catch (err) {
            console.error('[ERROR] Adding comment:', err);
            setError('Error adding comment.');
        }
    };

    // ✅ Add Reply (Using logged-in user)
    const addReply = async (commentId) => {
        if (!replyContent[commentId]?.trim()) return;
        if (!isAuthenticated || !user?.id) {
            setError('You must be logged in to reply.');
            return;
        }

        try {
            console.log('[POST] Adding reply...');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/blog/comment/${commentId}/reply`,
                { content: replyContent[commentId], author: user.username }, // ✅ Send username
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            console.log('[SUCCESS] Reply added:', response.data);
            setBlog((prevBlog) => ({
                ...prevBlog,
                comments: prevBlog.comments.map((comment) =>
                    comment._id === commentId
                        ? { ...comment, replies: [...comment.replies, response.data] }
                        : comment
                ),
            }));

            setReplyContent((prevReplies) => ({
                ...prevReplies,
                [commentId]: '',
            }));

            setShowReplyInput((prev) => ({ ...prev, [commentId]: false }));
            setExpandedReplies((prev) => ({ ...prev, [commentId]: true }));
        } catch (err) {
            console.error('[ERROR] Adding reply:', err);
            setError('Error adding reply.');
        }
    };

    // ✅ Expand/Collapse Replies
    const toggleReplyExpand = (commentId) => {
        setExpandedReplies((prevState) => ({
            ...prevState,
            [commentId]: !prevState[commentId],
        }));
    };

    // ✅ Toggle Reply Input Field
    const toggleReplyInput = (commentId) => {
        setShowReplyInput((prevState) => ({
            ...prevState,
            [commentId]: !prevState[commentId],
        }));
    };

    if (loading) return <div className="loading-container"><h1>Loading...</h1><div className="spinner"></div></div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!blog) return <div className="not-found-message">Blog not found</div>;

    return (
        <div className="blog-detail-container">
            <h1 className="blog-detail-title">{blog.title}</h1>

            {blog.images && blog.images.length > 0 && (
                <img 
                    src={`${process.env.REACT_APP_API_URL}${blog.images[0]}`}  // Updated to use environment variable
                    alt="Blog Cover" 
                    className="blog-detail-image"
                />
            )}
            <p className="mini-void"></p>
            <p className="blog-detail-byline"><strong>By:</strong> {blog.author || 'Anonymous'}</p>
            <div 
                className="blog-detail-content" 
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content) }} 
            />
            <p className="blog-detail-category"><strong>Category:</strong> {blog.category}</p>
            <p className="blog-detail-date">
                <strong>Published on:</strong> {format(new Date(blog.createdAt), 'MMMM dd, yyyy')}
            </p>

            {/* Comments Section */}
            <div className="comments-section">
                <h2 className="comments-title">Comments</h2>

                {blog.comments && blog.comments.length > 0 ? (
                    blog.comments.map((comment) => (
                        <div key={comment._id} className="comment-card">
                            <p className="comment-author">
                                <strong>{comment.author || 'Anonymous'}:</strong> {comment.content}
                            </p>
                            {comment.replies && comment.replies.length > 0 && (
                                <>
                                    <button onClick={() => toggleReplyExpand(comment._id)} className="toggle-replies-button">
                                        {expandedReplies[comment._id]
                                            ? `Hide ${comment.replies.length} Replies`
                                            : `View ${comment.replies.length} Replies`}
                                    </button>
                                    {expandedReplies[comment._id] && (
                                        <div className="replies-section">
                                            {comment.replies.map((reply) => (
                                                <div key={reply._id} className="reply-card">
                                                    <p className="reply-author">
                                                        <strong>{reply.author || 'Anonymous'}:</strong> {reply.content}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="reply-form">
                                {!showReplyInput[comment._id] ? (
                                    <button className="blog-button" onClick={() => toggleReplyInput(comment._id)}>
                                        Add Reply
                                    </button>
                                ) : (
                                    <>
                                        <textarea
                                            placeholder="Add a reply..."
                                            value={replyContent[comment._id] || ''}
                                            onChange={(e) =>
                                                setReplyContent((prevReplies) => ({
                                                    ...prevReplies,
                                                    [comment._id]: e.target.value,
                                                }))
                                            }
                                            className="reply-textarea"
                                        />
                                        <button onClick={() => addReply(comment._id)} className="blog-button">
                                            Submit Reply
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="no-comments">No comments yet. Be the first to comment!</p>
                )}

                {/* Add Comment Section */}
                {isAuthenticated && (
                    <div className="comment-form">
                        <textarea
                            placeholder="Add a comment..."
                            value={commentContent}
                            onChange={(e) => setCommentContent(e.target.value)}
                            className="comment-textarea"
                        />
                        <button onClick={addComment} className="blog-button">Submit Comment</button>
                    </div>
                )}
            </div>
        </div>
    );   
};

export default BlogDetail;
